'use client';

import { useEffect, useEffectEvent, useRef } from 'react';

import {
  useProjectResponseModeContext,
  type ProjectResponseModeEvent,
} from '@/shared-survey';
import { QuestionType } from '@/survey-graphql';

import SurveyAiFormsVoiceButton from './survey-ai-forms-voice-button';
import type { VoiceInputProps } from './survey-ai-forms-voice.types';

/**
 * Voice input component with speech-to-text functionality
 * Uses AssemblyAI for accurate, cross-browser voice recognition
 *
 * Features:
 * - Global voice on/off toggle - voice stays active across all questions
 * - Continue transcript from manual edits
 * - Voice persists when switching questions
 * - User can edit transcript while voice is active
 *
 * @requires ProjectResponseModeProvider must wrap the component tree
 *
 * @example
 * ```tsx
 * <SurveyAiFormsVoiceInput
 *   value={currentValue}
 *   isActive={isQuestionActive}
 *   onTranscriptChange={(text) => setValue('question1', text)}
 *   onFocus={() => setActiveQuestion('question1')}
 *   instanceId="question-1"
 * />
 * ```
 */
export default function SurveyAiFormsVoiceInput({
  value,
  isActive,
  onTranscriptChange,
  onFocus,
  registerStopCallback,
  className,
  hideLabel,
  instanceId,
  loading = false,
  isFormSection = false,
  questionType,
  disabled = false,
}: VoiceInputProps) {
  const previousQuestionIdRef = useRef<string | null | undefined>(instanceId);

  const {
    isListening,
    responseMode,
    toggleResponseMode,
    manuallyEditTranscript,
    tokenLoading,
    isStarting,
    eventsObservable,
  } = useProjectResponseModeContext();

  const isSupported = !tokenLoading;

  const onQuestionChanged = useEffectEvent(
    (
      event: Extract<ProjectResponseModeEvent, { type: 'question_changed' }>
    ) => {
      if (event.questionId === previousQuestionIdRef.current) return;

      previousQuestionIdRef.current = event.questionId;

      if (isActive && isListening) {
        manuallyEditTranscript(value || '');
      }
    }
  );

  const onListeningStateChanged = useEffectEvent(
    (
      event: Extract<
        ProjectResponseModeEvent,
        { type: 'listening_state_changed' }
      >
    ) => {
      if (event.isListening || !isActive) return;
      const transcriptToApply = event.finalTranscript;
      if (transcriptToApply.trim() && onTranscriptChange) {
        onTranscriptChange(transcriptToApply);
      }
    }
  );

  useEffect(() => {
    const subscription = eventsObservable.subscribe((event) => {
      switch (event.type) {
        case 'question_changed':
          onQuestionChanged(event);
          break;
        case 'listening_state_changed':
          onListeningStateChanged(event);
          break;
      }
    });
    return () => subscription.unsubscribe();
  }, [eventsObservable]);

  // Register stop callback for cleanup
  useEffect(() => {
    if (!registerStopCallback) return;

    if (isActive && isListening) {
      registerStopCallback(() => {
        // When stopping, switch to text mode (like toggling off)
        if (responseMode === 'voice') {
          toggleResponseMode();
        }
      });
    } else {
      registerStopCallback(null);
    }

    return () => {
      registerStopCallback(null);
    };
  }, [
    isActive,
    isListening,
    registerStopCallback,
    responseMode,
    toggleResponseMode,
  ]);

  const handleToggle = () => {
    if (isStarting || disabled) {
      return;
    }

    onFocus?.();
    toggleResponseMode();

    const willBeVoiceMode = responseMode === 'text';
    const isOpenEnded = questionType === QuestionType.OpenEnded;

    if (willBeVoiceMode) {
      const hasExistingValue = Boolean(value?.trim());
      if (hasExistingValue) {
        manuallyEditTranscript(value);
      }
    } else {
      if (isFormSection && !isOpenEnded) {
        onTranscriptChange?.('');
      }
    }
  };

  return (
    <SurveyAiFormsVoiceButton
      isListening={isListening}
      isSupported={isSupported}
      error={null}
      onToggle={handleToggle}
      className={className}
      hideLabel={hideLabel}
      loading={loading}
      isFormSection={isFormSection}
      responseMode={responseMode}
      disabled={disabled}
    />
  );
}
